<script lang="ts">
export default {
  name: 'SaveCurrentFilterModal',
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import savedPolicyFilterService from '@/services/v2/SavedPolicyFilterService';
import type { StoredFilters } from '@/types/v2/SavedPolicyFilter';

const props = defineProps<{
  show: boolean;
  filters: StoredFilters;
  columns: string[] | undefined;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'cancel'): void;
}>();

const filterName = ref('');
const includeColumns = ref(true);
const nameError = ref('');
const saving = ref(false);

function resetForm() {
  filterName.value = '';
  includeColumns.value = true;
  nameError.value = '';
  saving.value = false;
}

function handleCancel() {
  resetForm();
  emit('cancel');
}

async function handleSave() {
  nameError.value = '';

  if (!filterName.value.trim()) {
    nameError.value = 'Filter name is required';
    return;
  }

  saving.value = true;
  try {
    await savedPolicyFilterService.create({
      name: filterName.value.trim(),
      filters: JSON.stringify(props.filters),
      columns: includeColumns.value && props.columns ? JSON.stringify(props.columns) : null,
    });
    resetForm();
    emit('saved');
  } catch (error: unknown) {
    if (error instanceof Error && (error.cause as { status?: number })?.status === 400) {
      nameError.value = 'A saved filter with this name already exists';
    } else {
      nameError.value = 'An error occurred while saving. Please try again.';
    }
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AppModal
    :show="show"
    title="Save Current Filters"
    actionButtonLabel="Save"
    :handleCancel="handleCancel"
    :handleAction="handleSave"
    :loading="saving"
    :actionDisabled="saving"
    :showOverflow="true"
    size="sm">
    <template #content>
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">Filter Name</label>
          <input
            v-model="filterName"
            type="text"
            class="input input-bordered input-sm w-full"
            :class="{ 'input-error': nameError }"
            placeholder="Enter a name for this filter"
            @keyup.enter="handleSave" />
          <p v-if="nameError" class="text-xs text-error">{{ nameError }}</p>
        </div>
        <label class="flex cursor-pointer items-center gap-2">
          <input
            v-model="includeColumns"
            type="checkbox"
            class="checkbox checkbox-sm checkbox-primary" />
          <span class="text-sm text-gray-700">Include columns in filter</span>
        </label>
      </div>
    </template>
  </AppModal>
</template>
